import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { cloneDeep, get, set } from "lodash-es";
import type { StyleProp, ViewStyle } from "../../platformComponents";
import { StyleSheet, Text, View } from "../../platformComponents";
import {
  baseStyles,
  colors,
  fontFamily,
  fontWeight,
  margin,
  fontSize,
  flexDirection,
} from "../../styles/common";
import { AsInput } from "../AsInput";
import { KycSelect } from "../KycSelect";
import { KycDateTimePicker } from "../KycDateTimePicker";
import { KycRadioGroup } from "../KycRadioGroup";
import { KycCheckboxGroup } from "../KycCheckboxGroup";
import { ListedEntityForm } from "../ListedEntityForm";
import { StakeholdersForm } from "../StakeholdersForm";
import { BeneficiariesForm } from "../BeneficiariesForm";
import { DirectorsForm } from "../DirectorsForm";
import { TaxResidenceForm } from "../TaxResidenceForm";
import { WealthSourceOfOwnerForm } from "../WealthSourceOfOwnerForm";
import { IndividualSourceOfFundForm } from "../IndividualSourceOfFundForm";
import { IndividualSourceOfWealthForm } from "../IndividualSourceOfWealthForm";
import { InstitutionSourceOfFundForm } from "../InstitutionSourceOfFundForm";
import { GenericFormProvider, useGenericFormContext } from "./GenericFormContext";
import type {
  IFieldConfig,
  IFieldGroup,
  TFieldOptionConfig,
  TFieldOrGroup,
  TGenericFormType,
  TValidationRules,
} from "@adgm/cross-models";
import { CollapsibleSection } from "../../collapsibleSection";
import { dateFormat, i18nCrossUtil, IS_WEB } from "@adgm/libs";
import { CUSTOM_RADIO_OTHER_VALUES, EMPTY_ARRAY } from "@adgm/cross-config";
import { KycCategoryRadioGroup } from "../KycCategoryRadioGroup";

// 嵌套配置类型
interface INestedFormConfig<T> {
  fields: TFieldOrGroup[];
  validationRules: TValidationRules;
  initialData: T;
}

// 单层配置类型
interface IFlatFormConfig<T> {
  fields: IFieldConfig<any>[];
  validationRules: TValidationRules;
  initialData: T;
}

// 联合类型，支持两种配置
type TFormConfig<T> = INestedFormConfig<T> | IFlatFormConfig<T>;

interface IGenericFormProps<T> {
  config: TFormConfig<T> | TFieldOrGroup[];
  style?: any;
  onChange?: (data: T) => void;
  validationRules?: TValidationRules;
  initialData?: T;
  onProgressChange?: (progress: { completed: number; total: number }) => void;
  // 根据不同的Form类型，渲染不同的组件/样式
  genericFormType: TGenericFormType;
}

export interface IGenericFormRef<T> {
  checkData: () => { hasError: boolean; data: T };
}

function GenericForm<T extends Record<string, any>>(
  {
    config,
    style,
    onChange,
    validationRules,
    initialData,
    onProgressChange,
    genericFormType,
  }: IGenericFormProps<T>,
  ref: React.Ref<IGenericFormRef<T>>,
) {
  const intl = i18nCrossUtil.t();

  // 处理配置格式
  const normalizedConfig = Array.isArray(config)
    ? { fields: config, validationRules: validationRules!, initialData: initialData! }
    : config;

  const [data, setData] = useState<T>(cloneDeep(normalizedConfig.initialData));
  // 用来存储过滤之后的表单数据,比如将表单数据中的other选项过滤掉,用来暴露给外界用来存储
  const newExternalData = useRef<T>(cloneDeep(normalizedConfig.initialData));
  const [error, setError] = useState<Partial<Record<string, string>>>({});

  // 判断是否为分组
  const isGroup = (field: TFieldOrGroup): field is IFieldGroup => {
    return typeof field === "object" && "type" in field && field.type === "group";
  };

  // 获取字段值
  const getFieldValue = useCallback(
    (fieldPath: string, groupName?: string) => {
      // 特殊处理：如果字段名与group名相同，则省略group路径
      if (groupName && fieldPath.endsWith(`.${groupName}`)) {
        const directPath = groupName;
        return get(data, directPath);
      }
      return get(data, fieldPath);
    },
    [data],
  );

  /**
   * 清理特殊常量值，用于对外传递数据时过滤掉内部使用的特殊标识
   * @param val - 需要清理的值，支持字符串、数组、对象等类型
   * @returns 清理后的值
   *
   * 处理逻辑：
   * - 字符串类型：如果是 CUSTOM_RADIO_OTHER_VALUES 中的值，返回空字符串
   * - 数组类型：过滤掉 CUSTOM_RADIO_OTHER_VALUES 中的值
   * - 其他类型：直接返回原值
   */
  const cleanValue = useCallback((val: any) => {
    if (typeof val === "string") {
      if (CUSTOM_RADIO_OTHER_VALUES.includes(val)) {
        return "";
      }
    } else if (Array.isArray(val)) {
      return val.filter(item => !CUSTOM_RADIO_OTHER_VALUES.includes(item));
    }
    return val;
  }, []);

  const setFieldValue = useCallback(
    (fieldPath: string, value: any, groupName?: string) => {
      setData(prevData => {
        const newData = cloneDeep(prevData);
        const newFilterData = cloneDeep(newExternalData.current);
        // 特殊处理：如果字段名与group名相同，则省略group路径
        if (groupName && fieldPath.endsWith(`.${groupName}`)) {
          const directPath = groupName;
          set(newData, directPath, value);
          set(newFilterData, directPath, cleanValue(value));
        } else {
          set(newData, fieldPath, value);
          set(newFilterData, fieldPath, cleanValue(value));
        }

        newExternalData.current = newFilterData;
        onChange?.(newFilterData);
        return newData;
      });
    },
    [onChange],
  );

  // 检查字段是否有值的通用函数,并不检测值是否有效,比如输入框仅仅检测不为空
  const hasValue = useCallback(
    (value: any, fieldType: IFieldConfig<T>["type"], fieldConfig: IFieldConfig<T>): boolean => {
      if (!value) return false;
      value = cleanValue(value);
      switch (fieldType) {
        case "checkbox":
        case "checkboxWithInput":
        case "shareholders":
        case "beneficiaries":
        case "directors":
        case "taxResidences":
        case "fundSources":
        case "authorisedSignatories":
          // 数组类型：检查数组长度
          return Array.isArray(value) && value.length > 0;
        case "listedEntity":
          // 对象类型：检查对象中所有字段的值是否都填写了
          if (typeof value !== "object") return false;
          return Object.values(value).every(val => {
            if (typeof val === "string") return val.trim() !== "";
            return val !== undefined && val !== null;
          });
        case "categoryRadioGroup": {
          // 对象类型：检查 categoryOptions 中的每个 key 是否都有有效值
          if (typeof value !== "object" || value === null) return false;
          // 检查 categoryOptions 中的每个 key 是否都在 value 中存在，且值不为空
          //@ts-ignore
          const categoryOptions = fieldConfig?.categoryOptions;
          if (!Array.isArray(categoryOptions) || categoryOptions.length === 0) return false;
          // 检查每个分类的 key 是否都在 value 中存在，且值是非空字符串
          return categoryOptions.every(category => {
            const categoryKey = category?.key;
            if (!categoryKey) return false;
            const categoryValue = value[categoryKey];
            return (
              categoryValue !== undefined &&
              categoryValue !== null &&
              String(categoryValue).trim() !== ""
            );
          });
        }
        case "wealthSourceOfOwner":
        case "institutionSourceOfFund":
        case "individualSourceOfFund":
        case "individualSourceOfWealth": {
          // 对象类型：检查对象不为空，且对象中每个值（子对象）的所有字段都不为空
          if (typeof value !== "object" || value === null) return false;
          // 不能是空对象
          const keys = Object.keys(value);
          if (keys.length === 0) return false;
          // 检查对象中每个值（子对象）的所有字段是否都不为空
          return Object.values(value).every(subValue => {
            if (typeof subValue !== "object" || subValue === null) return false;
            // 检查子对象中的所有字段值是否都不为空
            return Object.values(subValue).every(val => {
              if (typeof val === "string") return val.trim() !== "";
              return val !== undefined && val !== null;
            });
          });
        }
        default:
          // 字符串类型：检查是否为空字符串
          if (typeof value === "string") return value.trim() !== "";
          return true;
      }
    },
    [],
  );

  // 检查字段值是否有效的通用函数,不仅检测是否有值,同时检测是否值有效
  const isValidValue = useCallback(
    (
      value: any,
      fieldType?: IFieldConfig<T>["type"],
      rule?: any,
      fieldConfig?: IFieldConfig<T>,
    ): boolean => {
      // 只校验必填字段
      if (!rule?.required || !fieldConfig || !fieldType) {
        return true; // 非必填字段直接返回 true
      }
      // 首先检查是否有值
      if (!hasValue(value, fieldType, fieldConfig)) return false;

      // 如果是字符串类型，进行额外的校验（min、max、pattern）
      if (fieldType === "input" || !fieldType) {
        if (rule?.min && value.length < rule.min) return false;
        if (rule?.max && value.length > rule.max) return false;
        if (rule?.pattern && !rule.pattern.test(value)) return false;
      }

      return true;
    },
    [hasValue],
  );

  // 验证字段
  const validateField = useCallback(
    (
      fieldPath: string,
      value: any,
      validationRules: TValidationRules,
      fieldConfig?: IFieldConfig<any>,
    ) => {
      // 优先使用字段配置中的验证规则
      let rules = fieldConfig?.validation;

      // 如果没有字段配置的验证规则，则使用全局验证规则
      if (!rules) {
        rules = validationRules[fieldPath];

        // 如果没找到，尝试用简单字段名查找（去掉group前缀）
        if (!rules) {
          const simpleFieldName = fieldPath.split(".").pop();
          if (simpleFieldName && simpleFieldName !== fieldPath) {
            rules = validationRules[simpleFieldName];
          }
        }
      }
      if (!rules) return "";
      for (const rule of rules) {
        const fieldType = fieldConfig?.type;

        // 使用 isValidValue 函数统一处理所有校验逻辑
        if (!isValidValue(value, fieldType, rule, fieldConfig)) {
          return rule.message || intl["kyc.field.required"];
        }
      }
      return "";
    },
    [isValidValue],
  );

  const handleValueChange = useCallback(
    (fieldPath: string, value: any, groupName?: string, fieldConfig?: IFieldConfig<any>) => {
      setFieldValue(fieldPath, value, groupName);
      const errMsg = validateField(fieldPath, value, normalizedConfig.validationRules, fieldConfig);
      setError(prev => ({ ...prev, [fieldPath]: errMsg }));
    },
    [setFieldValue, validateField, normalizedConfig.validationRules],
  );

  // 使用 useRef 缓存每个字段的处理函数
  const fieldHandlersRef = useRef<Record<string, (value: any) => void>>({});

  const getFieldHandler = useCallback(
    (fieldPath: string, groupName?: string, fieldConfig?: any) => {
      if (!fieldHandlersRef.current[fieldPath]) {
        fieldHandlersRef.current[fieldPath] = (value: any) =>
          handleValueChange(fieldPath, value, groupName, fieldConfig);
      }
      return fieldHandlersRef.current[fieldPath];
    },
    [handleValueChange],
  );

  // 通用的字段遍历和验证逻辑
  const traverseAndValidateFields = useCallback(
    (
      fields: TFieldOrGroup[],
      groupPath = "",
      groupName?: string,
      onFieldValidation?: (fieldPath: string, value: any, errMsg: string) => void,
    ) => {
      let completed = 0;
      let total = 0;
      const curError: Partial<Record<string, string>> = {};
      let hasError = false;

      const traverse = (
        fieldList: TFieldOrGroup[],
        currentGroupPath = "",
        currentGroupName?: string,
      ) => {
        fieldList.forEach(field => {
          if (isGroup(field)) {
            // 如果是分组，递归验证分组内的字段
            const nextGroupPath = currentGroupPath
              ? `${currentGroupPath}.${field.name}`
              : field.name;
            traverse(field.fields, nextGroupPath, field.name);
          } else {
            // 如果是普通字段，构建完整路径并验证
            const fieldPath = currentGroupPath ? `${currentGroupPath}.${field.name}` : field.name;
            const value = getFieldValue(fieldPath, currentGroupName);
            const errMsg = validateField(fieldPath, value, normalizedConfig.validationRules, field);

            total++;
            // 使用通用的 hasValue 函数检查字段是否已填写
            if (hasValue(value, field.type as IFieldConfig<T>["type"], field as IFieldConfig<T>)) {
              completed++;
            }

            if (errMsg) {
              curError[fieldPath] = errMsg;
              hasError = true;
            }

            // 调用回调函数，让调用方处理验证结果
            onFieldValidation?.(fieldPath, value, errMsg);
          }
        });
      };

      traverse(fields, groupPath, groupName);

      return { completed, total, curError, hasError };
    },
    [
      normalizedConfig.fields,
      getFieldValue,
      validateField,
      normalizedConfig.validationRules,
      hasValue,
    ],
  );

  // 检查数据有效性
  const checkData = useCallback(() => {
    const { curError, hasError } = traverseAndValidateFields(normalizedConfig.fields);
    setError(curError);
    return { hasError, data };
  }, [data, traverseAndValidateFields]);

  // 计算表单进度
  const calculateProgress = useCallback(() => {
    const { completed, total } = traverseAndValidateFields(normalizedConfig.fields);
    return { completed, total };
  }, [traverseAndValidateFields]);

  useEffect(() => {
    if (onProgressChange) {
      const progress = calculateProgress();
      onProgressChange(progress);
    }
  }, [calculateProgress]);

  useImperativeHandle(ref, () => ({
    checkData,
  }));

  // 渲染分组标题
  const renderGroupTitle = (group: IFieldGroup) => (
    <View style={{ flexDirection: flexDirection.column }}>
      <Text style={styles.groupLabel}>{group.label}</Text>
      {group.subLabel ? <Text style={styles.groupSubLabel}>{group.subLabel}</Text> : null}
    </View>
  );

  // 渲染字段
  const renderField = useCallback(
    (field: IFieldConfig<any>, groupPath = "", optionConfig?: TFieldOptionConfig) => {
      const { groupName, horizontalLayout = false } = optionConfig || {};
      const fieldPath = groupPath ? `${groupPath}.${field.name}` : field.name;
      const value = getFieldValue(fieldPath, groupName);
      const fieldError = error[fieldPath];
      const fieldRequired = field.validation?.some(v => v.required);

      const fieldValueChangeHandler = getFieldHandler(fieldPath, groupName, field);

      const label = field.label;

      switch (field.type) {
        case "input":
          return (
            <AsInput
              key={fieldPath}
              tip={field.tip}
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              label={label}
              value={value || ""}
              error={fieldError}
              onValueChange={fieldValueChangeHandler}
              trim={field.trim}
              valueType={field.valueType}
              horizontalLayout={horizontalLayout}
            />
          );
        case "radio":
        case "radioWithSelect":
        case "radioWithInput":
          return (
            <KycRadioGroup
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              required={fieldRequired}
              error={fieldError}
              key={fieldPath}
              label={label}
              onValueChange={fieldValueChangeHandler}
              options={field.options || EMPTY_ARRAY}
              customSelectOptions={field.subOptions || EMPTY_ARRAY}
              style={field.style as StyleProp<ViewStyle>}
              value={value || undefined}
              horizontalLayout={horizontalLayout}
            />
          );
        case "checkbox":
        case "checkboxWithInput":
          return (
            <KycCheckboxGroup
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              required={fieldRequired}
              error={fieldError}
              key={fieldPath}
              label={label}
              onValueChange={fieldValueChangeHandler}
              options={field.options || EMPTY_ARRAY}
              style={field.style as StyleProp<ViewStyle>}
              value={Array.isArray(value) ? value : []}
              horizontalLayout={horizontalLayout}
            />
          );
        case "select":
          return (
            <KycSelect
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              required={fieldRequired}
              key={fieldPath}
              label={label}
              value={value || undefined}
              error={fieldError}
              onValueChange={fieldValueChangeHandler}
              options={field.options || EMPTY_ARRAY}
              style={field.style as StyleProp<ViewStyle>}
              horizontalLayout={horizontalLayout}
              showSearch
              multiple={Boolean(field.multiple)}
            />
          );
        case "datePicker":
          return (
            <KycDateTimePicker
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              dateFormat={dateFormat}
              label={label}
              value={value}
              error={fieldError}
              onValueChange={fieldValueChangeHandler}
              style={field.style as StyleProp<ViewStyle>}
              horizontalLayout={horizontalLayout}
            />
          );
        case "percentage":
          return (
            <View key={fieldPath} style={{ flexDirection: "row", alignItems: "center" }}>
              <AsInput
                required={fieldRequired}
                sequenceNumber={field.fieldSequenceNumber}
                tip={field.tip}
                error={fieldError}
                label={label}
                onValueChange={fieldValueChangeHandler}
                style={{ flex: 1, marginRight: 8 }}
                value={value || ""}
                horizontalLayout={horizontalLayout}
              />
              <Text style={baseStyles.fontBold}>{field.suffix || "%"}</Text>
            </View>
          );
        case "listedEntity":
          return (
            <ListedEntityForm
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              label={label}
              value={value}
              error={fieldError}
              onChange={fieldValueChangeHandler}
            />
          );
        case "shareholders":
          return (
            <StakeholdersForm
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              value={value}
              onChange={fieldValueChangeHandler}
              error={fieldError}
              label={label}
              style={field.style as StyleProp<ViewStyle>}
            />
          );
        case "beneficiaries":
          return (
            <BeneficiariesForm
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              value={value}
              onChange={fieldValueChangeHandler}
              error={fieldError}
              label={label}
              subLabel={field.subLabel}
              style={field.style as StyleProp<ViewStyle>}
            />
          );
        case "directors":
          return (
            <DirectorsForm
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              value={value}
              onChange={fieldValueChangeHandler}
              error={fieldError}
              label={label}
              style={field.style as StyleProp<ViewStyle>}
              isAuthorisedSignatory={false}
            />
          );
        case "authorisedSignatories":
          return (
            <DirectorsForm
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              value={value}
              onChange={fieldValueChangeHandler}
              error={fieldError}
              label={label}
              style={field.style as StyleProp<ViewStyle>}
              isAuthorisedSignatory
            />
          );

        case "wealthSourceOfOwner":
          return (
            <WealthSourceOfOwnerForm
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              value={value}
              onChange={fieldValueChangeHandler}
              error={fieldError}
              label={label}
              subLabel={field.subLabel}
              style={field.style as StyleProp<ViewStyle>}
            />
          );
        case "individualSourceOfFund":
          return (
            <IndividualSourceOfFundForm
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              value={value}
              onChange={fieldValueChangeHandler}
              error={fieldError}
              label={label}
              style={field.style as StyleProp<ViewStyle>}
            />
          );
        case "institutionSourceOfFund":
          return (
            <InstitutionSourceOfFundForm
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              value={value}
              onChange={fieldValueChangeHandler}
              error={fieldError}
              label={label}
              style={field.style as StyleProp<ViewStyle>}
            />
          );
        case "individualSourceOfWealth":
          return (
            <IndividualSourceOfWealthForm
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              value={value}
              onChange={fieldValueChangeHandler}
              error={fieldError}
              label={label}
              style={field.style as StyleProp<ViewStyle>}
            />
          );
        case "categoryRadioGroup": {
          return (
            <KycCategoryRadioGroup
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              value={value}
              categories={field.categoryOptions}
              onValueChange={fieldValueChangeHandler}
              error={fieldError}
              label={label}
              style={field.style as StyleProp<ViewStyle>}
              optionGap={field.optionGap}
            />
          );
        }
        case "taxResidences":
          return (
            <TaxResidenceForm
              required={fieldRequired}
              sequenceNumber={field.fieldSequenceNumber}
              tip={field.tip}
              key={fieldPath}
              value={value}
              onChange={fieldValueChangeHandler}
              error={fieldError}
              label={label}
              style={field.style as StyleProp<ViewStyle>}
            />
          );

        default:
          console.warn(`Unknown field type: ${field.type}`);
          return null;
      }
    },
    [getFieldValue, error, handleValueChange],
  );

  // 渲染字段或分组
  const renderFieldOrGroup = useCallback(
    (fieldOrGroup: TFieldOrGroup, groupPath = "") => {
      if (isGroup(fieldOrGroup)) {
        // 渲染分组
        const currentGroupPath = groupPath
          ? `${groupPath}.${fieldOrGroup.name}`
          : fieldOrGroup.name;

        // 判断是否为特殊分组（仅在Web端应用特殊布局）
        const isSpecialGroup =
          IS_WEB && fieldOrGroup.type === "group" && fieldOrGroup.horizontalLayout;

        return (
          <View key={fieldOrGroup.name}>
            <CollapsibleSection
              style={{ marginBottom: 0 }}
              contentStyle={{ marginBottom: 0, paddingBottom: 0 }}
              collapsible={!IS_WEB}
              titleNode={renderGroupTitle(fieldOrGroup)}
              defaultCollapsed={false}>
              {isSpecialGroup ? (
                // 设计要求特殊分组：必须前6个字段两列布局，其余垂直布局。
                <>
                  {/* 前6个字段：两列布局 */}
                  <View style={styles.twoColumnLayout}>
                    {fieldOrGroup.fields.slice(0, 6).map(field => (
                      <View key={field.name} style={styles.columnItem}>
                        {renderField(field, currentGroupPath, {
                          horizontalLayout: fieldOrGroup.horizontalLayout,
                          groupName: fieldOrGroup.name,
                        })}
                      </View>
                    ))}
                  </View>

                  {/* 第7个字段开始：垂直布局 */}
                  {fieldOrGroup.fields.slice(6).map(field => (
                    <View key={field.name} style={styles.verticalItem}>
                      {renderField(field, currentGroupPath, {
                        horizontalLayout: fieldOrGroup.horizontalLayout,
                        groupName: fieldOrGroup.name,
                      })}
                    </View>
                  ))}
                </>
              ) : (
                // 普通分组：垂直布局
                fieldOrGroup.fields.map(field =>
                  renderField(field, currentGroupPath, {
                    horizontalLayout: fieldOrGroup.horizontalLayout,
                    groupName: fieldOrGroup.name,
                  }),
                )
              )}
            </CollapsibleSection>
          </View>
        );
      }

      // 渲染普通字段
      return renderField(fieldOrGroup, groupPath);
    },
    [renderGroupTitle, renderField],
  );

  return (
    <GenericFormProvider genericFormType={genericFormType}>
      <View style={style}>
        {normalizedConfig.fields.map(fieldOrGroup => renderFieldOrGroup(fieldOrGroup))}
      </View>
    </GenericFormProvider>
  );
}

const styles = StyleSheet.create({
  twoColumnLayout: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  columnItem: {
    width: "48%",
    marginBottom: margin.m13,
  },
  verticalItem: {
    width: "100%",
    marginBottom: margin.m13,
  },
  groupLabel: {
    fontSize: IS_WEB ? fontSize.f24 : fontSize.f16,
    fontFamily: IS_WEB ? "Common-Bold" : fontFamily.commonBold,
    fontWeight: fontWeight.f600,
    color: colors.white,
  },
  groupSubLabel: {
    fontSize: IS_WEB ? fontSize.f18 : fontSize.f14,
    fontFamily: IS_WEB ? "Common-Regular" : fontFamily.commonRegular,
    color: IS_WEB ? colors.white6 : colors.white18,
    marginTop: IS_WEB ? margin.m28 : margin.m12,
    marginBottom: IS_WEB ? margin.m12 : margin.m6,
  },
});

const ForwardedGenericForm = forwardRef(GenericForm) as <T extends Record<string, any>>(
  props: IGenericFormProps<T> & { ref?: React.Ref<IGenericFormRef<T>> },
) => React.ReactElement;
export default React.memo(ForwardedGenericForm);
export { useGenericFormContext };
